import React, { useState, ReactElement, useEffect, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { A11y, EffectFade } from 'swiper'
import type { Swiper as SwiperInstance } from 'swiper'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { useMediaQuery } from '@material-ui/core'

import { mehrAchtunugFontFamilies } from '@config/theme/definitions/fontFamily'
import InViewAnimation from '@objects/inViewAnimation'
import Container from '@objects/container'

import { getOptions, getResults } from '@services/api/autobahnVotingPhase1'

import { Colors } from '@config/theme/palette'

import background from './static/mehrAchtung-bg.svg'

import 'swiper/css'
import 'swiper/css/effect-fade'

import IconLabel from './components/icon-label'
import Intro from './components/0-intro'
import Voting from './components/1-voting'
import Message from './components/2-message'
import Result from './components/3-result'
import { AutobahnplakateTextsProvider, AutobahnplakateTexts } from './store'
import { hasVotedInStorage } from './helper/storage'

export const SWIPER_SPEED_MS = 400

const useStyles = makeStyles((theme) => ({
  root: {
    '@global': {
      '@font-face': mehrAchtunugFontFamilies,
    },
    fontFamily: theme.typography.fontFamily2,

    '& textarea:focus-visible, & button:focus-visible, & input:focus-visible': {
      outline: `2px solid ${theme.palette.button.main}`,
      outlineOffset: '2px',
    },
  },
  carousel: {
    '&.swiper': {
      overflow: 'visible',
    },
  },
  slide: {
    boxSizing: 'border-box',
  },
  background: {
    position: 'absolute' as const,
    transform: 'translate(42%, -30%) scale(0.7)',
    top: 0,
    right: 0,
    zIndex: -1,
    transition:
      'opacity 0.8s ease-in-out, transform 2s cubic-bezier(0.25, 1.3, 0.5, 1)',

    [theme.breakpoints.up('sm')]: {
      transform: 'translate(20%, -24%) scale(0.85)',
    },
    [theme.breakpoints.up('md')]: {
      transform: 'translate(45%, -15%) scale(1)',
    },
    [theme.breakpoints.up('lg')]: {
      transform: 'translate(23%, -18%) scale(1)',
    },
  },
  backgroundBig: {
    transform: 'scale(1.2) rotate(-10deg) translate(26%, -6%)',
    opacity: 0.5,

    [theme.breakpoints.up('sm')]: {
      transform: 'scale(1.25) rotate(-10deg) translate(-4%, -16%)',
    },
    [theme.breakpoints.up('md')]: {
      transform: 'scale(1.2) rotate(-10deg) translate(12%, -14%)',
    },

    '@media (min-width: 890px)': {
      transform: 'scale(1.25) rotate(-10deg) translate(2%, -13%)',
    },
  },
  container: {
    position: 'relative',
    contain: 'paint',
    border: `2px solid ${Colors.green[500]}`,
    [theme.breakpoints.up('lg')]: {
      borderRadius: theme.spacing(3),
      padding: theme.spacing(0, 8, 0, 8),
    },
  },
  logos: {
    position: 'absolute',
    top: theme.spacing(4),
    left: theme.spacing(4),
    display: 'flex',
    gap: theme.spacing(4),
    alignItems: 'center',
    [theme.breakpoints.up('lg')]: {
      top: theme.spacing(8),
      left: theme.spacing(8),
    },
  },
  logoRvG: {
    height: '30px',
    width: 'auto',
  },
  logoMA: {
    height: '40px',
    width: 'auto',
  },
  inner: {
    padding: theme.spacing(22, 0, 16, 0),

    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(30, 0, 16, 0),
    },
  },
}))

export type AutobahnplakateVotingPhase1Props = RvG.IReactDefaultProps & {
  introOverline: string
  introHeadline: string
  introHeadlineHighlightedWord: string
  introButtonLabel: string
  introButtonLabelVoted: string
  votingTopline: string
  votingHeadline: string
  votingHeadlineHighlightedWord: string
  votingItemLabel1: string
  votingItemLabel2: string
  votingItemLabel3: string
  votingItemLabel4: string
  votingItemLabel5: string
  votingButtonLabel: string
  messageTopline: string
  messageHeadline: string
  messageHeadlineHighlightedWord: string
  messageCopy: string
  messageTextareaPlaceholder: string
  messageTextareaErrorMessage: string
  messageSkipButtonLabel: string
  messageButtonLabel: string
  resultVotingHeadline: string
  resultVotingInfo: string
  resultTopline: string
  resultHeadline: string
  resultHeadlineSuccess: string
  resultEMailLabel: string
  resultEMailPlaceholder: string
  resultEMailErrorMessage: string
  resultEMailPrivacyInfo: RvG.Contentful.BasicRichTextType
  isAfterVoting: boolean
}

export default function AutobahnplakateVotingPhase1(
  props: AutobahnplakateVotingPhase1Props
): ReactElement {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [swiper, setSwiper] = useState<SwiperInstance | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [selectedVote, setSelectedVote] = useState<string | null>(null)
  const [options, setOptions] = useState([])
  const [results, setResults] = useState<
    Array<{ token: string; count: number }>
  >([])
  const [hasClientMounted, setHasClientMounted] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const intl = useIntl()
  const next = () => swiper?.slideNext()
  const prev = () => swiper?.slidePrev()
  const slideTo = (index: number) => swiper?.slideTo(index)
  const isVotingEnded = props.isAfterVoting
  const hasVotedEffective =
    isVotingEnded || (hasClientMounted && hasVotedInStorage())
  const showVotingSlide = !hasVotedEffective && !isVotingEnded
  const showMessageSlide =
    Boolean(props.messageTopline) && !isVotingEnded && !hasVotedEffective
  const resultSlideIndex =
    (props.introOverline ? 1 : 0) +
    (showVotingSlide ? 1 : 0) +
    (showMessageSlide ? 1 : 0)

  function updateSlideAccessibility(swiper: SwiperInstance) {
    swiper.slides.forEach((slide, index: number) => {
      const isVisible = index === swiper.activeIndex
      slide.setAttribute('aria-hidden', (!isVisible).toString())
      const focusable = slide.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]'
      )
      focusable.forEach((el) => {
        if (isVisible) {
          el.removeAttribute('tabindex')
        } else {
          el.setAttribute('tabindex', '-1')
        }
      })
    })
  }

  useEffect(() => {
    setHasClientMounted(true)
  }, [])

  useEffect(() => {
    const hasVoted = hasVotedInStorage() || isVotingEnded
    if (hasVoted) {
      getResults()
        .then((res) => {
          if (res && res.status === 200) {
            // only to get the nonce for further requests
            getOptions().then(() => {
              setResults(res.data)
            })
          }
        })
        .catch(console.error)
    } else {
      getOptions()
        .then((res) => {
          if (res && res.status === 200 && res.data) {
            setOptions(res.data)
          }
        })
        .catch(console.error)
    }
  }, [])

  useEffect(() => {
    if (!swiper || !hasClientMounted) {
      return
    }
    swiper.update()
  }, [swiper, hasClientMounted, hasVotedEffective, resultSlideIndex])

  return (
    <InViewAnimation>
      <AutobahnplakateTextsProvider
        value={
          {
            ...props,
            activeIndex,
            selectedVote,
            options,
            results,
            hasVotedEffective,
            resultSlideIndex,
            next,
            prev,
            slideTo,
            setResults,
            setSelectedVote,
          } as AutobahnplakateTexts
        }
      >
        <div className={classes.root} ref={rootRef} id="voting">
          <Container type="section" className={classes.container}>
            <img
              src={background}
              className={clsx(classes.background, {
                [classes.backgroundBig]: activeIndex > 0,
              })}
              alt=""
            />
            <div className={classes.logos}>
              <img
                src="/img/logo/mehrAchtung.svg"
                alt="Logo von #mehrAchtung"
                width="144"
                height="92"
                className={classes.logoMA}
              />
              <img
                src="/img/logo/rvg_logo.svg"
                alt="Logo von Runter vom Gas"
                height="18"
                className={classes.logoRvG}
              />
            </div>
            <div className={classes.inner}>
              <IconLabel activeIndex={activeIndex} />
              <Swiper
                onSwiper={(swiper) => {
                  setSwiper(swiper)
                  updateSlideAccessibility(swiper)
                }}
                onActiveIndexChange={(s: SwiperInstance) =>
                  setActiveIndex(s.realIndex)
                }
                onSlideChange={updateSlideAccessibility}
                slidesPerView={1}
                spaceBetween={0}
                preventClicks={false}
                preventClicksPropagation={false}
                allowTouchMove={false}
                simulateTouch={false}
                keyboard={{ enabled: false }}
                mousewheel={false}
                modules={[A11y, EffectFade]}
                effect="fade"
                fadeEffect={{ crossFade: true }}
                className={classes.carousel}
                speed={SWIPER_SPEED_MS}
                observer
                observeParents
                autoHeight
                a11y={{
                  enabled: true,
                  itemRoleDescriptionMessage: intl.formatMessage({
                    id: 'carousel.label.itemRoleDescriptionMessage',
                  }),
                  paginationBulletMessage: intl.formatMessage({
                    id: 'carousel.label.paginationBulletMessage',
                  }),
                  nextSlideMessage: intl.formatMessage({
                    id: 'carousel.label.nextSlideMessage',
                  }),
                  prevSlideMessage: intl.formatMessage({
                    id: 'carousel.label.prevSlideMessage',
                  }),
                  firstSlideMessage: intl.formatMessage({
                    id: 'carousel.label.firstSlideMessage',
                  }),
                  lastSlideMessage: intl.formatMessage({
                    id: 'carousel.label.lastSlideMessage',
                  }),
                  notificationClass: 'swiper-notification',
                }}
              >
                {props.introOverline && (
                  <SwiperSlide className={classes.slide}>
                    <Intro />
                  </SwiperSlide>
                )}
                {showVotingSlide && (
                  <SwiperSlide className={classes.slide}>
                    <Voting />
                  </SwiperSlide>
                )}
                {showMessageSlide && (
                  <SwiperSlide className={classes.slide}>
                    <Message />
                  </SwiperSlide>
                )}
                <SwiperSlide className={classes.slide}>
                  <Result />
                </SwiperSlide>
              </Swiper>
            </div>
          </Container>
        </div>
      </AutobahnplakateTextsProvider>
    </InViewAnimation>
  )
}
